import * as React from "react";
import { Button } from "react-bootstrap";
import { gettext } from "i18n";
import apiClient from "api";
import { useCurrentUser, useLogout } from "state/auth";
import BackupCodesDisplay from "components/auth/mfa/BackupCodesDisplay";
import MfaMethodSetup from "components/auth/mfa/MfaMethodSetup";
import { MfaStatus, describeRequirement, loadMfaStatus } from "utils/mfa";

/**
 * Shown instead of everything else while MFA is mandatory for the user but not
 * set up: the server refuses all other requests until it is
 */
const MfaSetupRequiredPage: React.FC = () => {
  const logout = useLogout();
  const retrieveCurrentUser = useCurrentUser(true);
  const [status, setStatus] = React.useState<MfaStatus | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [backupCodes, setBackupCodes] = React.useState<string[] | null>(null);

  React.useEffect(() => {
    loadMfaStatus()
      .then(setStatus)
      .catch((e) => setError(gettext((e as Error).message)));
  }, []);

  const finish = async () => {
    // The session's JWT still carries the restriction: get one without it
    apiClient.discardAuthJWT();
    await retrieveCurrentUser();
  };

  const onAdded = async (codes: string[] | null) => {
    if (codes) {
      setBackupCodes(codes);
    } else {
      await finish();
    }
  };

  return (
    <div className="MfaSetupRequiredPage">
      <h4 className="mb-3">{gettext("Set up multi-factor authentication")}</h4>
      {backupCodes ? (
        <>
          <p>{gettext("Multi-factor authentication is now on.")}</p>
          <BackupCodesDisplay codes={backupCodes} onDone={finish} />
        </>
      ) : (
        <>
          <p>
            {gettext(
              "Your account needs a second factor besides your password before you can continue.",
            )}
          </p>
          {status?.required_reasons.map((reason, index) => (
            <p key={`reason_${index}`} className="text-muted">
              {describeRequirement(reason)}
            </p>
          ))}
          {status?.methods.some((method) => !method.allowed) && (
            <p className="text-muted">
              {gettext(
                "Codes sent by email are not allowed for your account any more: please set up an authenticator app or a passkey.",
              )}
            </p>
          )}
          {error && <p className="text-danger">{error}</p>}
          {status && (
            <MfaMethodSetup
              allowedMethods={status.allowed_methods}
              existingMethods={status.methods.map((method) => method.kind)}
              onAdded={onAdded}
            />
          )}
        </>
      )}
      <hr />
      <Button variant="link" className="p-0" onClick={logout}>
        {gettext("Log out")}
      </Button>
    </div>
  );
};

export default MfaSetupRequiredPage;
